import { z } from 'zod';

import {
  IataSchema,
  MiceCorporateFieldSchema,
  MiceEventPremisesSchema,
  type MiceEventPremises,
} from './mice';
import { MiceInviteResponseSchema } from './mice_invite_response';
import { MiceTravelerFlightsSchema } from './mice_invite_flights';
import { EMPTY_MICE_PLACES, MiceEventPlacesSchema } from './mice_places';
import { MiceRsvpModeSchema } from './mice_estimate';
import { MiceAnswerSchema, MiceQuestionSchema } from './mice_questions';
import { MiceGenderSchema, MiceProfileFieldSchema } from './mice_profile';
import { MiceNamedRefSchema, NumericIdSchema } from './mice_refs';
import { MiceInviteRoomChangeSchema, MiceInviteRoomSchema } from './mice_rooms';
import { MiceInviteTransferSchema } from './mice_transfer';

// `mice_participants` importa de `mice_invite_response` (folha), nunca daqui: este arquivo importa `mice`.
export { MiceInviteResponseSchema, type MiceInviteResponse } from './mice_invite_response';

export const INVITE_DEADLINE_DAYS_BEFORE = 15;
export const INVITE_REMINDER_DAYS_BEFORE_DEADLINE = 3;

const DAY_MS = 86_400_000;

function toDay(date: string): number {
  return Date.parse(`${date}T00:00:00Z`) / DAY_MS;
}

function fromDay(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

/** Prazo padrão de resposta (datas civis YYYY-MM-DD): 15 dias antes do evento; se isso já passou,
 *  o dia anterior ao evento; se nem esse está no futuro, o próprio dia do evento. */
export function defaultInviteDeadline(eventDate: string, today: string): string {
  const event = toDay(eventDate);
  const early = event - INVITE_DEADLINE_DAYS_BEFORE;
  if (early > toDay(today)) return fromDay(early);
  if (event - 1 > toDay(today)) return fromDay(event - 1);
  return eventDate;
}

export const MiceJourneyStepSchema = z.enum(['origin', 'rules', 'flights']);
export type MiceJourneyStep = z.infer<typeof MiceJourneyStepSchema>;

export const MiceReturnModeSchema = z.enum(['same', 'other', 'none']);
export type MiceReturnMode = z.infer<typeof MiceReturnModeSchema>;

export const MiceJourneyDraftSchema = z.object({
  noFlight: z.boolean().default(false),
  origin: IataSchema.nullable().default(null),
  returnMode: MiceReturnModeSchema.default('same'),
  returnTo: IataSchema.nullable().default(null),
  preference: z.string().trim().max(500).default(''),
});
export type MiceJourneyDraft = z.infer<typeof MiceJourneyDraftSchema>;

/** A saída do viajante nunca é um aeroporto do evento: o envio a recusa (`origin_is_destination`). */
export function isEventAirport(
  premises: Pick<MiceEventPremises, 'destinations'> | null,
  iata: string | null,
): boolean {
  return iata !== null && premises?.destinations.some((d) => d.iata === iata) === true;
}

export const MiceJourneyViewSchema = MiceJourneyDraftSchema.extend({
  step: MiceJourneyStepSchema,
  done: z.boolean(),
});
export type MiceJourneyView = z.infer<typeof MiceJourneyViewSchema>;

export const MiceFlightNeedStateSchema = z.enum([
  'checking',
  'profile_missing',
  'choosing',
  'confirm',
  'no_option',
  'awaiting_organizer',
  'buying',
  'issued',
  'needs_help',
  'blocked',
]);
export type MiceFlightNeedState = z.infer<typeof MiceFlightNeedStateSchema>;

export const MiceInviteFlightNeedSchema = z.object({
  state: MiceFlightNeedStateSchema,
  missing: z.array(MiceProfileFieldSchema),
  flights: MiceTravelerFlightsSchema.nullable().default(null),
  vouchers: z
    .array(z.object({ direction: z.enum(['OUTWARD', 'RETURN']), voucherCode: z.string() }))
    .default([]),
  // A viagem do evento deste convidado (trip-api), quando existe e a flag do mice-api está
  // ligada; o web-chat a abre direto em vez de procurar a viagem pela lista.
  tripId: z.string().nullable().default(null),
  // O voo já foi decidido ou tem pedido: a recusa e "não preciso de voo" dão 409
  // `flights_in_progress`. É o que separa o `choosing` de uma reconsideração com pedido (#6627);
  // ausente (mice-api antigo), a tela decide só pelo estado.
  locked: z.boolean().optional(),
  // Em `choosing`: a escolha já aprovada (compra direta dentro do limite, ou o pedido que o
  // organizador aprovou) está sendo recotada antes da compra — não há nada sendo escolhido.
  // Ausente (mice-api antigo), a espera é a da escolha.
  confirming: z.boolean().optional(),
});
export type MiceInviteFlightNeed = z.infer<typeof MiceInviteFlightNeedSchema>;

/** Um dado corporativo que o convite pede ao viajante; `value` é o que ele já salvou. */
export const MiceInviteCorporateFieldSchema = z.object({
  field: MiceCorporateFieldSchema,
  value: MiceNamedRefSchema.nullable(),
});
export type MiceInviteCorporateField = z.infer<typeof MiceInviteCorporateFieldSchema>;

export const MiceInviteViewSchema = z.object({
  eventId: z.string(),
  eventName: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  venueName: z.string().nullable(),
  hotels: z.array(z.object({ name: z.string(), checkIn: z.string(), checkOut: z.string() })),
  organizerName: z.string(),
  companyName: z.string(),
  deadline: z.string(),
  response: MiceInviteResponseSchema.nullable(),
  respondedAt: z.string().nullable(),
  purchasesClosed: z.boolean(),
  premises: MiceEventPremisesSchema.nullable().default(null),
  places: MiceEventPlacesSchema.default(EMPTY_MICE_PLACES),
  journey: MiceJourneyViewSchema.nullable().default(null),
  flightNeed: MiceInviteFlightNeedSchema.nullable().default(null),
  /** Os campos que o passo "Dados da empresa" pede. Default: view de um mice-api antigo. */
  corporate: z.array(MiceInviteCorporateFieldSchema).default([]),
  /** As perguntas do evento ativas (só as opções ativas) e as respostas já gravadas do convidado.
   *  Default: view de um mice-api antigo. */
  questions: z.array(MiceQuestionSchema).default([]),
  answers: z.array(MiceAnswerSchema).default([]),
  /** Perguntas ativas que chamam o convidado de volta: sem resposta e obrigatórias ou criadas
   *  depois da última vez que ele gravou as respostas. Default: view de um mice-api antigo. */
  pendingQuestionIds: z.array(z.string()).default([]),
  /** Levantamento (`survey`): o convite termina no envio das respostas, sem regras nem voos.
   *  Default: view de um mice-api antigo. */
  rsvpMode: MiceRsvpModeSchema.default('purchase'),
  /** Passo de quartos do convite; nulo = evento sem quartos. Default: view de um mice-api antigo. */
  room: MiceInviteRoomSchema.nullable().default(null),
  /** Passo de transfer do convite; nulo = sem transfer ou não se aplica. Default: view de um mice-api antigo. */
  transfer: MiceInviteTransferSchema.nullable().default(null),
  /** A saída que o mesmo viajante enviou no convite mais recente de outro evento da org (sem
   *  "não preciso de voo"): a sugestão da saída. Default: view de um mice-api antigo. */
  lastOrigin: IataSchema.nullable().default(null),
});
export type MiceInviteView = z.infer<typeof MiceInviteViewSchema>;

/** Resposta do `POST .../room/gender`: a view do convite e o que a troca desfez. Default: um
 *  mice-api antigo, que só devolve a view. */
export const MiceInviteRoomGenderResponseSchema = MiceInviteViewSchema.extend({
  roomChange: MiceInviteRoomChangeSchema.nullable().default(null),
});
export type MiceInviteRoomGenderResponse = z.infer<typeof MiceInviteRoomGenderResponseSchema>;

export const MiceInviteReadRequestSchema = z.object({ travelerId: NumericIdSchema });
export type MiceInviteReadRequest = z.infer<typeof MiceInviteReadRequestSchema>;

export const MiceInviteRespondRequestSchema = z.object({
  travelerId: NumericIdSchema,
  response: MiceInviteResponseSchema,
});
export type MiceInviteRespondRequest = z.infer<typeof MiceInviteRespondRequestSchema>;

export const MiceJourneySaveRequestSchema = z.object({
  travelerId: NumericIdSchema,
  step: MiceJourneyStepSchema,
  draft: MiceJourneyDraftSchema,
});
export type MiceJourneySaveRequest = z.infer<typeof MiceJourneySaveRequestSchema>;

export const MiceJourneySubmitRequestSchema = z
  .object({ travelerId: NumericIdSchema, draft: MiceJourneyDraftSchema })
  .superRefine(({ draft }, ctx) => {
    if (draft.noFlight) return;
    if (draft.origin === null) {
      ctx.addIssue({ code: 'custom', path: ['draft', 'origin'], message: 'origin_required' });
    }
    if (draft.returnMode === 'other' && draft.returnTo === null) {
      ctx.addIssue({ code: 'custom', path: ['draft', 'returnTo'], message: 'return_to_required' });
    }
  });
export type MiceJourneySubmitRequest = z.infer<typeof MiceJourneySubmitRequestSchema>;

export const MiceJourneyProfileRequestSchema = z.object({
  travelerId: NumericIdSchema,
  gender: MiceGenderSchema.nullable(),
});
export type MiceJourneyProfileRequest = z.infer<typeof MiceJourneyProfileRequestSchema>;

export const MiceInviteCorporateOptionsRequestSchema = z.object({
  travelerId: NumericIdSchema,
  field: MiceCorporateFieldSchema,
  q: z.string().trim().max(120).default(''),
});
export type MiceInviteCorporateOptionsRequest = z.infer<
  typeof MiceInviteCorporateOptionsRequestSchema
>;

/** `suggested`: o padrão VOLL do centro de custo no catálogo, ou o único item. */
export const MiceInviteCorporateOptionsSchema = z.object({
  items: z.array(MiceNamedRefSchema),
  suggested: MiceNamedRefSchema.nullable(),
});
export type MiceInviteCorporateOptions = z.infer<typeof MiceInviteCorporateOptionsSchema>;

/** Erros do passo: `corporate_missing` (details `{ code, fields: MiceCorporateField[] }`),
 *  `corporate_locked` e `corporate_unknown`. */
export const MiceInviteCorporateSaveRequestSchema = z
  .object({
    travelerId: NumericIdSchema,
    costCenterId: z.string().min(1).optional(),
    motiveId: z.string().min(1).optional(),
    projectId: z.string().min(1).optional(),
  })
  .refine(
    (body) =>
      body.costCenterId !== undefined ||
      body.motiveId !== undefined ||
      body.projectId !== undefined,
    { message: 'Informe ao menos um dado corporativo' },
  );
export type MiceInviteCorporateSaveRequest = z.infer<typeof MiceInviteCorporateSaveRequestSchema>;

export const MiceOpenInviteSchema = z.object({
  eventId: z.string().min(1),
  eventName: z.string().min(1),
  startDate: z.string().min(1),
  deadline: z.string(),
  step: z.number().int().min(1).max(6),
  /** Passos do convite: 4 (no levantamento, 2: convite e saída), mais 1 com dados corporativos
   *  e mais 1 com perguntas do evento. Default: mice-api antigo. */
  total: z.number().int().min(2).max(6).default(4),
});
export type MiceOpenInvite = z.infer<typeof MiceOpenInviteSchema>;

export const MiceOpenInvitesSchema = z.object({ items: z.array(MiceOpenInviteSchema) });
export type MiceOpenInvites = z.infer<typeof MiceOpenInvitesSchema>;

export const MiceOpenInvitesRequestSchema = z.object({ travelerId: NumericIdSchema });
export type MiceOpenInvitesRequest = z.infer<typeof MiceOpenInvitesRequestSchema>;
